import cx from 'classnames'
import { baseKeymap, chainCommands, Command, toggleMark } from 'prosemirror-commands'
import { history, redo, undo } from 'prosemirror-history'
import { keymap } from 'prosemirror-keymap'
import { EditorState } from 'prosemirror-state'
import { EditorView } from 'prosemirror-view'
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'
import Styles from './TextBoxShape.module.scss'
import { textboxSchema } from './textboxSchema'
import {
  changeListIndent,
  deleteBackward,
  deleteForward,
  exitEmptyListItem,
  outdentAtStart,
  splitTextboxListItem,
} from './textboxListCommands'
import { buildRichTextDoc, flattenRichTextDoc } from './richTextDoc'
import {
  applyWeightToTransaction,
  clearActiveProseView,
  notifyProseSelectionChanged,
  setActiveProseView,
} from './activeProseView'
import { listInputRules } from './listInputRules'

export interface RichTextEditorHandle {
  view: () => EditorView | null
}

interface Props {
  richText?: Record<string, unknown> | null
  text?: string
  autoFocus?: boolean
  onChange: (richText: Record<string, unknown>, text: string) => void
  handleKeyDown?: (view: EditorView, event: KeyboardEvent) => boolean
}

// Bold shares the single font_weight channel (see activeProseView's ProseWeight), so Mod-b toggles
// font_weight 700<->400 rather than a separate `strong` mark — matching the Bold button and weight dropdown.
const toggleBoldWeight: Command = (state, dispatch) => {
  const fontWeight = textboxSchema.marks.font_weight
  const strong = textboxSchema.marks.strong
  const { empty, $from } = state.selection
  const active = (empty ? state.storedMarks : null) || $from.marks()
  const fw = fontWeight.isInSet(active)
  const isBold = (fw ? (fw.attrs.weight as number) >= 700 : false) || !!strong?.isInSet(active)
  if (dispatch) dispatch(applyWeightToTransaction(state.tr, state.selection, isBold ? 400 : 700))
  return true
}

const buildPlugins = () => [
  history(),
  listInputRules(textboxSchema),
  keymap({
    'Mod-b': toggleBoldWeight,
    'Mod-i': toggleMark(textboxSchema.marks.em),
    'Mod-u': toggleMark(textboxSchema.marks.underline),
    // Nesting is a flat `depth` attribute on the list item (changeListIndent), not nested <ul> DOM.
    // Enter exits an empty item (un-indent, then lift out) or splits a new row. Backspace at the start
    // of an item un-indents/lifts it. Delete + the rest fall through to the base keymap (joinForward /
    // joinBackward / deleteSelection). The scoped schema (doc: block+, list_item: paragraph) makes the
    // trailing-phantom and `li > [p, p]` strand bugs impossible, so no cleanup plugins are needed.
    Enter: chainCommands(exitEmptyListItem, splitTextboxListItem),
    Backspace: chainCommands(outdentAtStart, deleteBackward),
    Delete: deleteForward,
    Tab: changeListIndent(1),
    'Shift-Tab': changeListIndent(-1),
    'Mod-z': undo,
    'Mod-y': redo,
    'Mod-Shift-z': redo,
  }),
  keymap(baseKeymap),
]

export const RichTextEditor = forwardRef<RichTextEditorHandle, Props>(
  ({ richText, text, autoFocus, onChange, handleKeyDown }, forwardedRef) => {
    const ref = useRef<HTMLDivElement>(null)
    const viewRef = useRef<EditorView | null>(null)
    const onChangeRef = useRef(onChange)
    onChangeRef.current = onChange
    const handleKeyDownRef = useRef(handleKeyDown)
    handleKeyDownRef.current = handleKeyDown
    // Captured once: the editor is created on mount and driven imperatively after, so it must not
    // re-initialize when richText/text change (that would destroy the live view mid-edit).
    const seedRef = useRef({ richText, text, autoFocus })

    useEffect(() => {
      if (!ref.current) return
      const doc = buildRichTextDoc(seedRef.current.richText, seedRef.current.text)
      const state = EditorState.create({ schema: textboxSchema, doc, plugins: buildPlugins() })
      const view = new EditorView(ref.current, {
        state,
        handleKeyDown: (currentView, event) => {
          // tldraw's canvas keydown listener lives on the editor CONTAINER — a DOM ancestor of this
          // editable. If keystrokes bubble up to it, Backspace/Delete are treated as "delete the
          // selected shape", so editing a character removes the whole text box (and leaves tldraw
          // stuck in an editing state with no shape -> "Expected an editing shape!"). Stop them at the
          // editor; Escape / Cmd-Enter still complete editing via the handleKeyDown prop below.
          event.stopPropagation()
          return handleKeyDownRef.current?.(currentView, event) ?? false
        },
        handleDOMEvents: {
          // Same reasoning for keyup (tldraw also listens for keyup on the container).
          keyup: (_currentView, event) => {
            event.stopPropagation()
            return false
          },
          // Stop pointerdown/mousedown from reaching tldraw's canvas: on pointerdown tldraw calls
          // setPointerCapture on the canvas, which then steals every drag move from this editable —
          // so a mouse drag-selection can't extend across a block boundary (keyboard select works
          // because it never involves pointer capture). Returning false lets ProseMirror still place
          // the caret / start its own selection drag.
          mousedown: (_currentView, event) => {
            event.stopPropagation()
            return false
          },
          pointerdown: (_currentView, event) => {
            event.stopPropagation()
            return false
          },
          focus: () => {
            setActiveProseView(view)
            return false
          },
        },
        dispatchTransaction(transaction) {
          const newState = view.state.apply(transaction)
          view.updateState(newState)
          if (transaction.docChanged) {
            onChangeRef.current(newState.doc.toJSON(), flattenRichTextDoc(newState.doc))
          }
          notifyProseSelectionChanged()
        },
      })
      viewRef.current = view
      if (seedRef.current.autoFocus) view.focus()
      return () => {
        clearActiveProseView(view)
        view.destroy()
        viewRef.current = null
      }
    }, [])

    useImperativeHandle(
      forwardedRef,
      () => ({
        view: () => viewRef.current,
      }),
      [],
    )

    // `prose-input` opts out of the global proseEditor.scss .ProseMirror default so the shape font inherits.
    return <div ref={ref} className={cx('prose-input', Styles.richText, Styles.richTextEditor)} />
  },
)

RichTextEditor.displayName = 'RichTextEditor'
